import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NotificationService } from "../../services/Notification.service";
import { AuthV2Service } from "../../services/authV2Service";
import { useAuthV2ReCaptcha } from "../../hooks/useAuthV2ReCaptcha";
import { useStoreAuthV2 } from "../../store/AuthV2Store";

export const AuthV2RecoveryPanel = ({
  currentState = 1,
  panelState,
  setPanelState,
}) => {
  const { t } = useTranslation();
  const { executeRecovery } = useAuthV2ReCaptcha();
  const {
    identifier,
    identifierType,
    maskedSecondaryContact,
    setMaskedSecondaryContact,
    setSecondaryContactType,
    setRecoveryMode,
    setProspectId,
    setOtpState,
    setCurrentStep,
    setIdentifier,
    setBlocked,
    resetOtpAttempts,
    setTestOtpCode,
  } = useStoreAuthV2();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recovered, setRecovered] = useState(false);

  const handleRecover = async () => {
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      const captchaToken = await executeRecovery();
      const response = await AuthV2Service.recoverSecondaryContact(
        identifier,
        captchaToken,
      );

      const result = response.data;

      setMaskedSecondaryContact(result.secondaryContact);
      setSecondaryContactType(result.secondaryType);
      setProspectId(result.prospectId);
      setTestOtpCode(result.otpCode);
      setRecovered(true);
    } catch (err) {
      const errorMsg = err.message || "";

      if (errorMsg.includes("secondaire") || errorMsg.includes("secondary")) {
        setError("no_secondary");
      } else {
        setError("generic");
        NotificationService.addToQueue(
          2,
          errorMsg || t("modals.connexionPanel.authV2.genericError"),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = () => {
    setRecoveryMode(true);
    setCurrentStep("recovery");
    setBlocked(null);
    resetOtpAttempts();

    const cooldownDate = new Date(Date.now() + 60 * 1000);
    setOtpState(true, cooldownDate, null);

    setPanelState({
      ...panelState,
      authV2Recovery: 0,
      authV2Otp: 1,
    });
  };

  const handleBack = () => {
    setRecovered(false);
    setError(null);
    setMaskedSecondaryContact("");

    setPanelState({
      ...panelState,
      authV2Recovery: 2,
      authV2Otp: 1,
    });
  };

  const handleContactSupport = () => {
    window.open(
      "https://api.whatsapp.com/send?phone=+2250556094636&text=Bonjour%2C%20j%27ai%20besoin%20d%27aide%20pour%20la%20r%C3%A9cup%C3%A9ration%20de%20mon%20compte",
      "_blank",
    );
  };

  return (
    <div
      className={`contains inscription-part ${
        currentState === 0
          ? "go-left-side"
          : currentState === 1
            ? "come-center"
            : "go-right-side"
      } | flcolm`}
    >
      <button className="back-btn | btn" onClick={handleBack}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M12.5 15L7.5 10L12.5 5"
            stroke="var(--gray-900)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <span className="auth-v2-panel-title">
        {t("modals.connexionPanel.authV2.recoveryTitle")}
      </span>

      {/* Etat initial : pas encore récupéré */}
      {!recovered && !error && (
        <>
          <p className="auth-v2-subtitle">
            {t("modals.connexionPanel.authV2.recoverySubtitle", {
              channel:
                identifierType === "PHONE"
                  ? t("modals.connexionPanel.authV2.recoveryChannelEmail")
                  : t("modals.connexionPanel.authV2.recoveryChannelPhone"),
            })}
          </p>

          <div className="auth-v2-footer | flrow allcenter">
            <button
              onClick={handleContactSupport}
              className="btn-outline | btn flex"
            >
              {t("modals.connexionPanel.authV2.contactSupport")}
            </button>
            <button
              onClick={handleRecover}
              className="next-btn | btn flex"
              disabled={loading}
            >
              {loading
                ? t("modals.connexionPanel.authV2.recoverySending")
                : t("modals.connexionPanel.authV2.recoveryConfirm")}
            </button>
          </div>
        </>
      )}

      {/* Contact secondaire trouvé : affichage masqué + envoi OTP */}
      {recovered && (
        <>
          <p className="auth-v2-subtitle">
            {t("modals.connexionPanel.authV2.recoveryOtpSent")}
          </p>

          <div className="auth-v2-masked-contact">
            <span>{maskedSecondaryContact}</span>
          </div>

          <div className="auth-v2-footer | flrow allcenter">
            <button
              onClick={handleContactSupport}
              className="btn-outline | btn flex"
            >
              {t("modals.connexionPanel.authV2.contactSupport")}
            </button>
            <button onClick={handleSendOtp} className="next-btn | btn flex">
              {t("modals.connexionPanel.authV2.recoveryVerify")}
            </button>
          </div>
        </>
      )}

      {/* Erreur : pas de contact secondaire */}
      {error === "no_secondary" && (
        <>
          <p className="auth-v2-subtitle">
            {t("modals.connexionPanel.authV2.recoveryNoSecondary")}
          </p>

          <div className="auth-v2-footer | flrow allcenter">
            <button onClick={handleBack} className="btn-outline | btn flex">
              {t("modals.connexionPanel.authV2.recoveryBackToOtp")}
            </button>
            <button
              onClick={handleContactSupport}
              className="next-btn | btn flex"
            >
              {t("modals.connexionPanel.authV2.contactSupport")}
            </button>
          </div>
        </>
      )}

      {/* Erreur générique */}
      {error === "generic" && (
        <>
          <p className="auth-v2-subtitle">
            {t("modals.connexionPanel.authV2.genericError")}
          </p>

          <div className="auth-v2-footer | flrow allcenter">
            <button onClick={handleBack} className="btn-outline | btn flex">
              {t("modals.connexionPanel.authV2.recoveryBackToOtp")}
            </button>
            <button
              onClick={handleRecover}
              className="next-btn | btn flex"
              disabled={loading}
            >
              {t("modals.connexionPanel.authV2.recoveryRetry")}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
